import { setStepperValue } from './stepper.js';
import { setDropdown, resetDropdown } from './dropdown.js';
import { getClientData, setBookingStateProp } from '../state.js';

// Keep answers only for the booking being edited, in this page's memory.
let rememberedAnswers = {};
let extrasPrepared = false;

function primaryInput(extra) {
    return extra.querySelector('.atb-addon-value') || extra.querySelector('input[data-input="addon"]:not([data-additional-answer]):not([data-dropdown-option-answer])');
}

function rememberDisplayedAnswers() {
    if (!extrasPrepared) return;
    document.querySelectorAll('.atb-addon').forEach(extra => {
        const input = primaryInput(extra);
        if (!input || input.dataset.customAddonType) return;
        // Hidden extras may already have been reset while changing unit/filters.
        if (extra.classList.contains('d-none')) return;
        const id = input.dataset.entityId;
        const type = extra.classList.contains('dropdown-extra') ? 'dropdown'
            : extra.classList.contains('atb-stepper') ? 'stepper'
            : input.type === 'checkbox' ? 'checkbox' : 'text';
        const optionId = input.dataset.selectedId;
        const answer = extra.querySelector(`[data-dropdown-option-id="${optionId}"] input[data-dropdown-option-answer]`)
            || extra.querySelector('[data-additional-answer]');
        rememberedAnswers[id] = {
            input_type: type,
            addon_value: type === 'checkbox' ? Number(input.checked) : input.value,
            dropdown_option_id: optionId || null,
            addon_value2: answer?.value || ''
        };
    });
}

/**
 * Resets all extras/addon UI - used when wiping the stage UI when either a booking is added/destroyed or moving backwards in the booking process
 */
function resetExtrasUI(authoritative = false, discardRemembered = false) {
    if (discardRemembered) {
        rememberedAnswers = {};
        extrasPrepared = false;
    } else if (!authoritative) {
        rememberDisplayedAnswers();
        // Controls being cleared must not overwrite remembered answers on re-entry.
        extrasPrepared = false;
    }
    const extras = document.querySelectorAll('.atb-addon');            
    
    extras.forEach((extra) => {
        if (authoritative && extra.querySelector('[data-custom-addon-type]')) return;
        extra.querySelectorAll('[data-additional-answer], [data-dropdown-option-answer]').forEach(input => {
            input.value = '';
            if (input.dataset.dropdownOptionAnswer) input.required = false;
        });
        extra.querySelectorAll('.atb-addon-dropdown-answer').forEach(row => {
            row.classList.add('d-none');
            row.style.display = 'none';
        });

        if (extra.classList.contains('atb-stepper')) {
            setStepperValue(extra, 0);
            return;
        }

        if (extra.classList.contains('dropdown-extra')) {                 
            resetDropdown(extra.querySelector('.atb-select-input'));
            return;
        }
        
        const input = extra.querySelector('.atb-addon-value') || extra.querySelector('input:not([data-additional-answer]):not([data-dropdown-option-answer])');

        // Skip heading extras, no need to reset
        if (!input)
            return;

        if (authoritative && input.type === 'checkbox') {
            input.checked = input.disabled && input.defaultChecked;
            return;
        }

        if (
            input.type == 'checkbox' && 
            !input.disabled && 
            !input.required
        ) {
            input.checked = false;
            return;
        }

        if (input.type == 'text') {
            input.value = '';
            return;
        }
    });
}


/**
 * Given an object of keys and values representing extras, sets 
 * @param {object} extrasForBooking - key value pairs, the keys of which are addon IDs in the database and values the quantities of the corresponding addon
 */
function setExtrasUI(extrasForBooking) {
    Object.entries(extrasForBooking).forEach(([id, extraData]) => {
        const extra = document.querySelector(`.atb-addon[id="atb-addon-${id}"]`);
        const input = extra?.querySelector('.atb-addon-value') || extra?.querySelector('input[data-input="addon"]:not([data-additional-answer]):not([data-dropdown-option-answer])');
        if (!input) return;

        if (extraData.input_type === 'dropdown') {
            setDropdown(input, extraData.dropdown_option_id, false);
            extra.querySelectorAll('.atb-addon-dropdown-answer').forEach(row => {
                const selected = row.dataset.dropdownOptionId == extraData.dropdown_option_id;
                row.classList.toggle('d-none', !selected);
                row.style.display = selected ? '' : 'none';
                const answer = row.querySelector('input[data-dropdown-option-answer]');
                if (answer) {
                    answer.required = selected && answer.dataset.dropdownOptionMandatory === '1';
                    answer.value = selected ? (extraData.addon_value2 || '') : '';
                }
            });
        } else if (extraData.input_type === 'checkbox') {
            input.checked = Number(extraData.addon_value) > 0;
        } else if (extraData.input_type === 'stepper') {
            setStepperValue(extra, extraData.addon_value, false);
        } else {
            input.value = extraData.addon_value ?? '';
        }
        const additional = extra.querySelector('[data-additional-answer]');
        if (additional) additional.value = extraData.addon_value2 || '';
    });
}


async function prepareExtrasUI(bookingState) {
    rememberDisplayedAnswers();
    const selectedAddons = await extrasPricingAndAvailability();

    if (Array.isArray(selectedAddons)) {
        resetExtrasUI(true);
        const addonsState = {};
        selectedAddons.forEach(addon => {
            const el = document.getElementById(`atb-addon-${addon.id}`);
            if (!el) return;
            let inputType;
            if (el.classList.contains('dropdown-extra')) inputType = 'dropdown';
            else if (el.classList.contains('atb-stepper')) inputType = 'stepper';
            else inputType = el.querySelector('input[type="checkbox"]') ? 'checkbox' : 'text';
            addonsState[addon.id] = {
                input_type: inputType,
                addon_value: addon.status,
                addon_value2: addon.value2 || '',
                dropdown_option_id: addon.dropdown_option || null
            };
        });
        setBookingStateProp('addons', addonsState);
        setExtrasUI(addonsState);
    }

    if (bookingState.is_single_unit) {
        togglePetFriendlyExtras(bookingState.unit, bookingState.pets_filter_active);
    } else {
        const subUnits = getClientData().sub_units;
        const subUnit = subUnits.find(su => parseIntb10(su.id) == bookingState.sub_unit);
        const doShowPetFriendlyExtras = bookingState.pets_filter_active &&
            (subUnit?.pets_flag == "1" || subUnit?.pets_flag_seasonal == "1");

        togglePetFriendlyExtras(bookingState.unit, doShowPetFriendlyExtras);
    }

    if (Array.isArray(selectedAddons)) {
        const restored = {};
        Object.entries(rememberedAnswers).forEach(([id, answer]) => {
            const extra = document.querySelector(`.atb-addon[id="atb-addon-${id}"]`);
            const input = extra && primaryInput(extra);
            if (!input || extra.dataset.checkoutApplicable === '0' || extra.classList.contains('d-none') || input.closest('.d-none')) return;
            if (answer.input_type === 'dropdown') {
                const option = Array.from(extra.querySelectorAll('li[data-value]'))
                    .find(option => option.dataset.value == answer.dropdown_option_id);
                if (!option || option.classList.contains('d-none') || option.getAttribute('aria-disabled') === 'true') return;
            }
            // Current session answers take precedence. Remembered values fill only
            // selections removed by a change of dates/unit/pitch.
            if (!selectedAddons.some(addon => String(addon.id) === id)) restored[id] = answer;
        });
        setExtrasUI(restored);
        extrasPrepared = true;
        rememberDisplayedAnswers();
    }
}

export { resetExtrasUI, setExtrasUI, prepareExtrasUI, rememberDisplayedAnswers }
